import { useState, type ReactElement } from 'react';
import type { WidgetProps } from '@captomi/schema-form';

// Console chips editor for array-of-string fields (service areas, specialties). Unlike the shared
// default, it SPLITS commas/newlines into separate chips — so "fremont, oakland, san jose" (typed
// or pasted) becomes three entries, not one — and de-dupes case-insensitively.

const chip: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: 'var(--green-50, #eef6f0)',
  border: '1px solid var(--line)',
  borderRadius: 999,
  padding: '2px 6px 2px 10px',
  fontSize: 13,
};
const chipX: React.CSSProperties = {
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontSize: 15,
  lineHeight: 1,
  color: 'var(--text-2)',
};
const inputStyle: React.CSSProperties = { flex: 1, padding: 6, border: '1px solid var(--line)', borderRadius: 6 };

function split(raw: string): string[] {
  return raw.split(/[,\n]/).map((s) => s.trim()).filter((s) => s.length > 0);
}

export function ChipsWidget({ value, onChange }: WidgetProps): ReactElement {
  const items = Array.isArray(value) ? (value as string[]) : [];
  const [draft, setDraft] = useState('');

  const add = (raw: string): void => {
    const next = [...items];
    for (const p of split(raw)) {
      if (!next.some((x) => x.toLowerCase() === p.toLowerCase())) next.push(p);
    }
    if (next.length !== items.length) onChange(next);
    setDraft('');
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
        {items.map((it, i) => (
          <span key={`${it}-${i}`} style={chip}>
            {it}
            <button
              type="button"
              aria-label={`remove ${it}`}
              style={chipX}
              onClick={() => onChange(items.filter((_, j) => j !== i))}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          style={inputStyle}
          value={draft}
          placeholder="Type and press Enter, or paste a comma-separated list"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter or comma commits the current draft (the comma itself isn't kept).
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add(draft);
            }
          }}
          onBlur={() => draft.trim() && add(draft)}
        />
        <button type="button" className="btn secondary" onClick={() => add(draft)}>
          Add
        </button>
      </div>
    </div>
  );
}
